import { useMemo, useState } from 'react';
import {
  ArcElement,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Filler,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip as ChartTooltip,
} from 'chart.js';
import { Bar, Doughnut, Line } from 'react-chartjs-2';
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  InputAdornment,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import type { ChartData, Kpi, ManagementFilters, ManagementReportKey, ReportRow, ReportTable } from '../../api/managementReports';
import { formatValue } from './formatValue';

ChartJS.register(CategoryScale, LinearScale, BarElement, PointElement, LineElement, ArcElement, ChartTooltip, Legend, Filler);

const PALETTE = ['#5B2DA3', '#12B76A', '#F79009', '#0E7490', '#E5484D', '#2F6FED', '#8E4EC6', '#6B7280'];

/** KPI cards; a card with a supporting report opens it (filters kept). */
export const KpiCards = ({ kpis, onOpen }: { kpis: Kpi[]; onOpen: (report: ManagementReportKey) => void }) => (
  <Box sx={{ display: 'grid', gap: 1.5, mb: 2.5, gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)', lg: 'repeat(5, 1fr)' } }}>
    {kpis.map((k) => {
      const body = (
        <CardContent sx={{ p: 1.75, '&:last-child': { pb: 1.75 } }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.3 }}>
            {k.label}
          </Typography>
          <Typography
            variant="h5"
            sx={{ fontWeight: 700, color: k.status === 'unavailable' ? 'text.disabled' : 'primary.main', mt: 0.5 }}
            data-testid={`kpi-${k.key}`}
          >
            {k.value === null && k.status === 'unavailable' ? 'Unavailable' : formatValue(k.value, k.format)}
          </Typography>
          {k.status && (
            <Tooltip title={k.note ?? ''}>
              <Chip
                size="small"
                color={k.status === 'incomplete' ? 'warning' : 'default'}
                variant="outlined"
                label={k.status === 'incomplete' ? 'Incomplete' : 'No data'}
                sx={{ mt: 0.5, height: 20, fontSize: 11 }}
              />
            </Tooltip>
          )}
          {k.previous !== undefined && (
            <Typography variant="caption" sx={{ display: 'block', lineHeight: 1.3, mt: 0.5, color: 'text.secondary' }} data-testid={`kpi-${k.key}-previous`}>
              Previous period: {formatValue(k.previous, k.format)}
              {k.value !== null && k.previous !== null && k.value !== k.previous && (
                <Box component="span" sx={{ ml: 0.5, fontWeight: 700, color: k.value > k.previous ? 'success.main' : 'error.main' }}>
                  {k.value > k.previous ? '▲' : '▼'}
                </Box>
              )}
            </Typography>
          )}
          {k.hint && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.3, mt: 0.25 }}>
              {k.hint}
            </Typography>
          )}
        </CardContent>
      );
      return (
        <Card key={k.key} variant="outlined" sx={{ height: '100%' }}>
          {k.report ? (
            <CardActionArea sx={{ height: '100%', alignItems: 'flex-start' }} onClick={() => onOpen(k.report!)} aria-label={`${k.label}: open details`}>
              {body}
            </CardActionArea>
          ) : (
            body
          )}
        </Card>
      );
    })}
  </Box>
);

const CHART_TITLES: Record<string, string> = {
  funnel: 'Conversion funnel',
  monthly: 'Monthly trend',
  byBranch: 'By branch',
  ranking: 'Counsellor ranking (top 8)',
  reasons: 'Dropout reasons',
  byUniversity: 'By university',
  comparison: 'Comparison (largest 12)',
  stages: 'Where registered students are now',
  targets: 'Target vs achieved, by university (intakes in the period)',
  achievement: 'Target vs achieved',
  byCountry: 'By country',
  revenueByUniversity: 'Revenue by university',
  byIntake: 'By intake',
  examStatus: 'FIS exam status',
};

const ChartCard = ({ name, chart }: { name: string; chart: ChartData }) => {
  const empty = chart.labels.length === 0 || chart.series.every((s) => s.data.every((v) => v === 0));
  const datasets = chart.series.map((s, i) => ({
    label: s.label,
    data: s.data,
    backgroundColor: name === 'stages' || name === 'reasons' || name === 'examStatus' ? chart.labels.map((_, j) => PALETTE[j % PALETTE.length]) : PALETTE[i % PALETTE.length],
    borderColor: PALETTE[i % PALETTE.length],
    tension: 0.3,
    fill: false,
  }));
  const data = { labels: chart.labels, datasets };
  const common = { responsive: true, maintainAspectRatio: false, plugins: { legend: { position: 'bottom' as const } } };
  const money = chart.format === 'currency';
  const ticks = money ? { callback: (v: string | number) => formatValue(Number(v), 'currency').replace(/\.00$/, '') } : { precision: 0 };
  return (
    <Card variant="outlined" sx={{ height: '100%' }}>
      <CardContent>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
          {CHART_TITLES[name] ?? name}
        </Typography>
        <Box sx={{ height: 260, position: 'relative' }} role="img" aria-label={CHART_TITLES[name] ?? name}>
          {empty ? (
            <Stack alignItems="center" justifyContent="center" sx={{ height: '100%' }}>
              <Typography variant="body2" color="text.secondary">
                No data for these filters.
              </Typography>
            </Stack>
          ) : name === 'stages' || name === 'reasons' || name === 'examStatus' ? (
            <Doughnut data={data} options={common} />
          ) : name === 'monthly' ? (
            <Line data={data} options={{ ...common, scales: { y: { beginAtZero: true, ticks: { precision: 0 } } } }} />
          ) : (
            <Bar
              data={data}
              options={{
                ...common,
                indexAxis: name === 'funnel' || name === 'ranking' ? 'y' : 'x',
                plugins: { legend: { display: chart.series.length > 1, position: 'bottom' } },
                scales: money
                  ? { x: { beginAtZero: true }, y: { beginAtZero: true, ticks } }
                  : { x: { beginAtZero: true, ticks: { precision: 0 } }, y: { beginAtZero: true, ticks: { precision: 0 } } },
              }}
            />
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export const ReportCharts = ({ charts }: { charts: Record<string, ChartData> }) => (
  <Box sx={{ display: 'grid', gap: 2, mb: 2.5, gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' } }}>
    {Object.entries(charts).map(([name, chart]) => (
      <ChartCard key={name} name={name} chart={chart} />
    ))}
  </Box>
);

const drillOf = (row: ReportRow): Partial<ManagementFilters> | null => {
  const entries = Object.entries(row).filter(([k, v]) => k.startsWith('drill_') && typeof v === 'number');
  return entries.length ? Object.fromEntries(entries.map(([k, v]) => [k.slice(6), v])) : null;
};

/** A report table: search, sort by any column, pages of 25. Hierarchical
 * tables (with subtotal and total lines) keep their order; a line with
 * drill-down filters opens the same report filtered to it. */
export const ReportTableCard = ({ table, onDrill }: { table: ReportTable; onDrill?: (filters: Partial<ManagementFilters>) => void }) => {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [sort, setSort] = useState<{ key: string; dir: 'asc' | 'desc' } | null>(null);
  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    const filtered = q ? table.rows.filter((r) => Object.values(r).some((v) => String(v ?? '').toLowerCase().includes(q))) : table.rows;
    if (!sort || table.hierarchical) return filtered;
    return [...filtered].sort((a, b) => {
      const x = a[sort.key];
      const y = b[sort.key];
      const cmp = typeof x === 'number' && typeof y === 'number' ? x - y : String(x ?? '').localeCompare(String(y ?? ''));
      return sort.dir === 'asc' ? cmp : -cmp;
    });
  }, [table.rows, table.hierarchical, search, sort]);
  const numeric = (f?: string) => f === 'number' || f === 'percent' || f === 'currency';
  const count = table.rows.filter((r) => !r.rowType).length;

  return (
    <Card variant="outlined" sx={{ mb: 2 }}>
      <CardContent>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between" alignItems={{ sm: 'center' }} sx={{ mb: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            {table.title}{' '}
            <Typography component="span" variant="body2" color="text.secondary">
              ({count})
            </Typography>
          </Typography>
          {table.rows.length > 10 && (
            <TextField
              size="small"
              placeholder="Search…"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(0);
              }}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                },
              }}
            />
          )}
        </Stack>
        {table.description && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {table.description}
          </Typography>
        )}
        <TableContainer sx={{ maxWidth: '100%' }}>
          <Table size="small" aria-label={table.title}>
            <TableHead>
              <TableRow>
                {table.columns.map((c) => (
                  <TableCell key={c.key} align={numeric(c.format) ? 'right' : 'left'} sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
                    {table.hierarchical ? (
                      c.label
                    ) : (
                      <TableSortLabel
                        active={sort?.key === c.key}
                        direction={sort?.key === c.key ? sort.dir : 'asc'}
                        onClick={() => setSort((s) => ({ key: c.key, dir: s?.key === c.key && s.dir === 'asc' ? 'desc' : 'asc' }))}
                      >
                        {c.label}
                      </TableSortLabel>
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={table.columns.length}>
                    <Typography variant="body2" color="text.secondary">
                      Nothing to show for these filters.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((r, i) => {
                const drill = onDrill ? drillOf(r) : null;
                return (
                  <TableRow
                    key={i}
                    hover
                    data-row-type={r.rowType ?? 'detail'}
                    onClick={drill ? () => onDrill!(drill) : undefined}
                    title={drill ? 'Show only this line' : undefined}
                    sx={(theme) => ({
                      ...(drill ? { cursor: 'pointer' } : {}),
                      ...(r.rowType === 'subtotal' ? { bgcolor: alpha(theme.palette.primary.main, 0.08), '& td': { fontWeight: 700 } } : {}),
                      ...(r.rowType === 'total' ? { bgcolor: alpha(theme.palette.primary.main, 0.18), '& td': { fontWeight: 800 } } : {}),
                    })}
                  >
                    {table.columns.map((c) => (
                      <TableCell key={c.key} align={numeric(c.format) ? 'right' : 'left'}>
                        {formatValue(r[c.key], c.format)}
                      </TableCell>
                    ))}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        {rows.length > 25 && (
          <TablePagination
            component="div"
            count={rows.length}
            page={page}
            onPageChange={(_e, p) => setPage(p)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(Number(e.target.value));
              setPage(0);
            }}
            rowsPerPageOptions={[25, 50, 100]}
          />
        )}
      </CardContent>
    </Card>
  );
};

export const NotesCard = ({ notes }: { notes: string[] }) => (
  <Card variant="outlined" sx={{ bgcolor: 'action.hover' }}>
    <CardContent>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
        How these figures are counted
      </Typography>
      <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
        {notes.map((n) => (
          <Typography key={n} component="li" variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
            {n}
          </Typography>
        ))}
      </Box>
    </CardContent>
  </Card>
);

